import { defineStore } from 'pinia';
import { useMainStore } from '@/stores/mainStore';
import { useBookingStore } from '@/stores/bookingStore';
import UserService from '@/services/user.service';
import Commande_Inscription from "@/classes/Commande/Commande_Inscription.class.js";
import List_Options from "@/classes/Options/List_Options.class.js";
import List_Activites from "@/classes/Activites/List_Activites.class.js";
import List_Articles_Boutique from "@/classes/Articles/List_Articles_Boutique.class.js";
import AlerteSMS from "@/classes/AlerteSMS/alerte_SMS.class.js";
import Amend_Commande_Option_Cart from "@/classes/Cart/Amend_Commande_Option_Cart.class.js";
import PublicService from '@/services/public.service';
export const useAmendBookingStore = defineStore('amendBooking', {
    state: () => ({
        id: null,
        uuid: null,
        cycle: null,
        exclude_taxe: null,
        id_account: null,
        id_module: null,
        nb_articles: 0,
        prix: 0,
        prix_total: 0,
        statut: null,
        taxe: 0,
        mail: null,
        list_commandes_inscriptions_coureur: [],
        account: null,
        cart: null,

    }),
    actions: {
        /**
         * Fetches the booking data for the current booking ID and updates the store.
         * 
         * @async
         * @function fetch
         * @returns {Promise<void>} A promise that resolves when the booking data has been fetched and set.
         */
        async fetch() {
            const response = await UserService.getCommande(this.id);
            this.set(response);
        },
        /**
         * Sets the properties of the booking store with the provided data.
         * 
         */
        set(data) {
            this.id = data.id;
            this.uuid = data.uuid;
            this.cycle = data.cycle;
            this.exclude_taxe = data.exclude_taxe;
            this.id_account = data.id_account;
            this.id_module = data.id_module;
            this.nb_articles = data.nb_articles;
            this.prix = data.prix;
            this.prix_total = data.prix_total;
            this.statut = data.statut;
            this.taxe = data.taxe;
            this.mail = data.mail;
            this.list_commandes_inscriptions_coureur = data.list_commandes_inscriptions_coureur;
            this.account = data.account;
            if (data.list_commandes_inscriptions_coureur) {
                this.list_commandes_inscriptions_coureur = [];
                for (let i = 0; i < data.list_commandes_inscriptions_coureur.length; i++) {
                    let commande_inscription = new Commande_Inscription();
                    commande_inscription.set(data.list_commandes_inscriptions_coureur[i]);
                    if (commande_inscription.is_not_deleted_or_transfered()) {
                        this.list_commandes_inscriptions_coureur.push(commande_inscription);
                    }

                }
            }
        },
        /**
         * Initializes the amendment data from the main store.
         * This method retrieves data from the main store and uses it to initialize
         * the lists of options, activities, and articles for each command in the
         * list of runner registrations.
         *
         * @function
         * @name initialize_amend_from_mainstore
         * @memberof amendBookingStore
         * @instance
         */
        initialize_amend_from_mainstore() {
            const mainStore = useMainStore();
            for (let i = 0; i < this.list_commandes_inscriptions_coureur.length; i++) {
                let list_options = new List_Options(mainStore.module.id);
                list_options.initialize_from_mainstore(mainStore.module.options.list);
                let list_activites = new List_Activites(mainStore.module.id);
                list_activites.initialize_from_mainstore(mainStore.module.activites.list);
                let list_articles = new List_Articles_Boutique(mainStore.module.id);
                list_articles.initialize_from_mainstore(mainStore.module.articles_boutique.list);
                list_articles.filter_availables_articles();
                this.list_commandes_inscriptions_coureur[i].list_options_amended = list_options.list;
                this.list_commandes_inscriptions_coureur[i].list_activites_amended = list_activites.list;
                this.list_commandes_inscriptions_coureur[i].list_articles_amended = list_articles.list;

                // Initialiser l'alerte SMS
                let alerte_sms = new AlerteSMS(mainStore.module.id);
                if (mainStore.module.has_alerte_sms) {
                    alerte_sms.set_propose(true);
                    // Si l'alerte SMS existe déjà sur cette commande_inscription, ne pas la proposer
                    if (this.list_commandes_inscriptions_coureur[i].alerte_sms && this.list_commandes_inscriptions_coureur[i].alerte_sms.valide == "1") {
                        alerte_sms.set_propose(false);
                    }
                }
                this.list_commandes_inscriptions_coureur[i].alerte_sms_amended = alerte_sms;
            }
        },

        /**
         * Initializes the cart with a new instance of Amend_Commande_Option_Cart.
         * Sets the cart's uuid_commande and id_commande properties based on the current object's uuid and id.
         */
        initCart() {
            this.cart = new Amend_Commande_Option_Cart();
            this.cart.uuid_commande = this.uuid;
            this.cart.id_commande = this.id;
        },

        /**
         * Clears the state of the booking store by resetting all properties to their default values.
         * 
         * @method
         * @name clearState
         * @memberof amendBookingStore
         * @instance
         */
        clearState() {
            this.id = null;
            this.uuid = null;
            this.cycle = null;
            this.exclude_taxe = null;
            this.id_account = null;
            this.id_module = null;
            this.nb_articles = 0;
            this.prix = 0;
            this.prix_total = 0;
            this.statut = null;
            this.taxe = 0;
            this.mail = null;
            this.list_commandes_inscriptions_coureur = [];
            this.account = null;
            this.cart = null;

        },

        /**
         * Updates the cart with the current list of runner registrations.
         * 
         */
        updateCart() {
            this.cart.update(this.list_commandes_inscriptions_coureur);
        },

        /**
         * Handles the process of posting a free checkout.
         * 
         * This function performs the following steps:
         * 1. Posts the current cart.
         * 2. Sets the cart as paid for a free checkout.
         * 3. If the payment request is successful, updates the booking store with the module ID and transaction details.
         * 
         * @returns {Promise<boolean>} A promise that resolves to a boolean indicating whether both the post and payment requests were successful.
         */
        async post_free_checkout() {
            const post_request = await this.cart.post();
            const paid_request = await this.cart.set_paid_free_checkout();
            if (paid_request) {
                try {
                    const bookingStore = useBookingStore();
                    bookingStore.id_module = this.id_module;
                    bookingStore.transaction = {};
                    bookingStore.transaction.commande_infos = {};
                    bookingStore.transaction.commande_infos.orderId = this.uuid;
                    bookingStore.transaction.commande_infos.amount = 0;
                } catch (error) {
                    return true;
                }


            }
            return post_request && paid_request;
        },

        /**
         * Sends a POST request using the cart's post method and returns the response.
         *
         * @returns {Promise<any>} The response from the POST request.
         */
        async post_default_checkout() {
            const post_request = await this.cart.post();
            return post_request;
        },
        async check_availabilities() {

            let res = await PublicService.checkCommandeAvailabilities(this.$state, null);
            if (!res) return { status: false, errors: ["Une erreur est survenue"] };

            return { status: res.checked, errors: res.errors };
        },

    },
    getters: {
        /**
         * Checks if there are items in the cart.
         *
         * @returns {boolean} True if there are items in the cart, false otherwise.
         */
        has_items_in_cart() {
            return this.cart?.nb_articles > 0;
        },
        /**
         * Checks if the booking exists by verifying if the UUID is not null.
         *
         * @returns {boolean} True if the UUID is not null, indicating the booking exists; otherwise, false.
         */
        exist() {
            return this.uuid != null;
        },
        /**
         * Checks if the booking store is loaded.
         *
         * @returns {boolean} Returns true if the booking store exists and the cart is loaded, otherwise false.
         */
        isLoaded() {
            return this.exist && this.cart;
        },
        /**
         * Checks if there is only one inscription command in the list.
         *
         * @returns {boolean} True if there is exactly one inscription command, false otherwise.
         */
        is_unique_inscription_commande() {
            return this.list_commandes_inscriptions_coureur.length == 1;
        },


    }
});